// Canonical benefits-app menu items, mirrored from benefits-mobile-app
// (LandingPage TAB_CONFIG, Profile MENU_CONFIG, CustomTopTabBar). These are the
// keys that can appear in benefitsOptions.primaryMenu / hamburgerMenu /
// secondaryMenu, with the display names the app shows.
//
// Icon convention (CustomTopTabBar.tsx, LandingPage.tsx): for a menu item key X
// the app reads the inactive icon from hamburgerMenuIcons[X] and the selected
// icon from hamburgerMenuIcons[`${X}_selected`].

export type MenuKind = 'primary' | 'hamburger' | 'secondary';

export interface BenefitsMenuItem {
  key: string;
  label: string;
  menus: MenuKind[];
}

export const BENEFITS_MENU_ITEMS: BenefitsMenuItem[] = [
  { key: 'myCard', label: 'My Card', menus: ['primary', 'hamburger'] },
  { key: 'myRewards', label: 'Rewards', menus: ['primary', 'hamburger'] },
  { key: 'shop', label: 'Shop', menus: ['primary', 'hamburger'] },
  { key: 'healthAdventures', label: 'Health Adventure', menus: ['primary', 'hamburger'] },
  { key: 'scan', label: 'Scan', menus: ['primary'] },
  { key: 'personal', label: 'Personal', menus: ['hamburger'] },
  { key: 'notifications', label: 'Notifications', menus: ['hamburger'] },
  { key: 'manageCard', label: 'Manage Card', menus: ['hamburger'] },
  { key: 'security', label: 'Security', menus: ['hamburger'] },
  { key: 'help', label: 'Help', menus: ['hamburger', 'secondary'] },
  { key: 'agreements', label: 'Agreements', menus: ['secondary'] },
  { key: 'privacyPolicy', label: 'Privacy Policy', menus: ['secondary'] },
  { key: 'officialRules', label: 'Official Rules', menus: ['secondary'] },
  { key: 'signOut', label: 'Sign Out', menus: ['secondary'] },
];

const BY_KEY: Record<string, BenefitsMenuItem> = Object.fromEntries(
  BENEFITS_MENU_ITEMS.map((i) => [i.key, i]),
);

/** Items valid in a given menu (in catalog order). */
export const itemsForMenu = (kind: MenuKind): BenefitsMenuItem[] =>
  BENEFITS_MENU_ITEMS.filter((i) => i.menus.includes(kind));

/** Human label for a key; falls back to the raw key for custom/unknown values. */
export const labelForKey = (key: string): string => BY_KEY[key]?.label ?? key;

/** True if the key is part of the canonical catalog. */
export const isKnownMenuKey = (key: string): boolean => BY_KEY[key] !== undefined;

/**
 * The two icon slots every menu item needs: the inactive icon (`X`) and the
 * selected icon (`${X}_selected`), with friendly labels. Preserves the order of
 * the given keys; de-duplicates.
 */
export const iconSlotsForKeys = (keys: string[]): { key: string; label: string }[] => {
  const seen = new Set<string>();
  const slots: { key: string; label: string }[] = [];
  for (const k of keys) {
    if (!k) continue;
    const name = labelForKey(k);
    for (const [slotKey, suffix] of [[k, '— icon'], [`${k}_selected`, '— selected']] as const) {
      if (seen.has(slotKey)) continue;
      seen.add(slotKey);
      slots.push({ key: slotKey, label: `${name} ${suffix}` });
    }
  }
  return slots;
};
